<template>
  <div>
    <dx-grid
      url="messages"
      :baseUrl="getBaseUrl()"
      name="wer-messages"
      :columns="columns"
      title="Messages"
      :selectionEnabled="true"
      :actions="actions"
      ref="grid"
      :onRowClicked="onRowClicked"
      navigationPath="onRowClicked"
      navigationField="onRowClicked"
      :onLoaded="onLoaded"
    ></dx-grid>
    <v-dialog v-model="newMessageDialog" max-width="600">
      <v-card class="new-msg-card">
        <!-- Header -->
        <div class="new-msg-header">
          <v-icon color="white" class="mr-2">mdi-message-plus</v-icon>
          <span>New Message</span>
        </div>

        <div class="new-msg-body">
          <!-- ── Message section ── -->
          <div class="new-msg-section">
            <div class="new-msg-section-label">
              <v-icon small class="mr-1" color="success"
                >mdi-volume-high</v-icon
              >
              Message Recording
            </div>

            <v-radio-group
              v-model="newMessage.recordingMode"
              row
              hide-details
              class="mt-1 mb-3"
              @change="onRecordingModeChanged"
            >
              <v-radio
                label="Upload MP3"
                value="upload"
                color="success"
              ></v-radio>
              <v-radio
                label="Write &amp; Generate"
                value="write"
                color="success"
              ></v-radio>
            </v-radio-group>

            <!-- Upload mode -->
            <div v-if="newMessage.recordingMode === 'upload'">
              <v-btn
                outline
                color="success"
                :loading="uploading"
                @click="pickFile()"
              >
                <v-icon left>mdi-cloud-upload</v-icon>
                Choose MP3 File
              </v-btn>
              <input
                type="file"
                style="display: none"
                ref="files"
                accept=".mp3"
                @change="onFilePicked($event)"
              />
              <div v-if="files.length" class="new-msg-filename mt-2">
                <v-icon small color="success" class="mr-1">mdi-music</v-icon>
                {{ files[0].name }}
              </div>
              <div v-if="uploadFileError" class="new-msg-error mt-2">
                <v-icon small color="error" class="mr-1"
                  >mdi-alert-circle</v-icon
                >
                {{ uploadFileError }}<br />
                Compress the file first:
                <a
                  href="https://www.freeconvert.com/mp3-compressor"
                  target="_blank"
                  rel="noopener noreferrer"
                  >FreeConvert</a
                >
                or
                <a
                  href="https://www.onlineconverter.com/compress-mp3"
                  target="_blank"
                  rel="noopener noreferrer"
                  >OnlineConverter</a
                >
              </div>
            </div>

            <!-- Write mode -->
            <div v-if="newMessage.recordingMode === 'write'">
              <v-textarea
                v-model="newMessage.recordingText"
                @input="onNewMessageRecordingTextChanged"
                rows="7"
                outline
                hide-details
                label="Type the message text here..."
                class="new-msg-textarea"
              ></v-textarea>
              <v-btn
                class="mt-3"
                :disabled="!newMessage.recordingText"
                :loading="messageGenerating"
                @click="generateNewMessageRecordingAudio"
                color="info"
              >
                <v-icon left>mdi-creation</v-icon>
                {{
                  newMessage.recordingURL && newMessage.recordingText
                    ? "Regenerate Audio"
                    : "Generate Audio"
                }}
              </v-btn>
              <div v-if="messageGenerating" class="new-msg-generating mt-2">
                <v-progress-linear
                  indeterminate
                  color="info"
                  height="3"
                ></v-progress-linear>
                <span class="caption mt-1 d-block"
                  >Generating message audio...</span
                >
              </div>
              <div v-if="messageGenerateError" class="new-msg-error mt-2">
                <v-icon small color="error" class="mr-1"
                  >mdi-alert-circle</v-icon
                >
                {{ messageGenerateError }}
              </div>
            </div>

            <!-- Audio player (both modes) -->
            <audio
              v-if="newMessage.recordingURL"
              controls
              ref="recordingURL"
              @loadedmetadata="onRecordingMetadataLoaded"
              :src="newMessage.recordingURL"
              class="new-msg-audio mt-3"
            ></audio>
          </div>

          <v-divider class="my-3"></v-divider>

          <!-- ── From Phone ── -->
          <div class="new-msg-section new-msg-phone-row">
            <div
              class="new-msg-section-label mb-0 mr-4"
              style="white-space:nowrap"
            >
              <v-icon small class="mr-1" color="success">mdi-phone</v-icon>
              From Phone
            </div>
            <div style="flex:1">
              <input
                v-model="newMessage.phone"
                @input="onPhoneInput"
                type="tel"
                class="new-msg-phone-input"
                :class="{
                  'new-msg-phone-error': newMessage.phone && !phoneValid,
                }"
                placeholder="e.g. 3479710707"
              />
              <div
                v-if="newMessage.phone && !phoneValid"
                class="new-msg-phone-hint"
              >
                Enter 10 or 11 digits (e.g. 3479710707 or 13479710707)
              </div>
            </div>
          </div>

          <v-divider class="my-3"></v-divider>

          <!-- ── Subject ── -->
          <div class="new-msg-section">
            <div class="new-msg-section-label">
              <v-icon small class="mr-1" color="success"
                >mdi-text-box-outline</v-icon
              >
              Subject
            </div>
            <v-textarea
              v-model="newMessage.subject"
              @input="onNewMessageSubjectChanged"
              rows="2"
              outline
              hide-details
              label="Enter subject text..."
              class="new-msg-textarea"
            ></v-textarea>
            <audio
              v-if="newMessage.subjectUrl"
              controls
              :src="newMessage.subjectUrl"
              class="new-msg-audio"
              style="margin-top: 8px;"
            ></audio>
            <div v-if="newMessage.subject" class="mt-2">
              <v-btn
                small
                :loading="subjectGenerating"
                @click="generateNewMessageSubjectAudio"
                color="info"
              >
                <v-icon left small>mdi-creation</v-icon>
                {{
                  newMessage.subjectUrl
                    ? "Regenerate Subject Audio"
                    : "Generate Subject Audio"
                }}
              </v-btn>
            </div>
            <div v-if="subjectGenerating" class="new-msg-generating mt-2">
              <v-progress-linear
                indeterminate
                color="info"
                height="3"
              ></v-progress-linear>
              <span class="caption mt-1 d-block"
                >Generating subject audio...</span
              >
            </div>
            <div v-if="subjectGenerateError" class="new-msg-error mt-2">
              <v-icon small color="error" class="mr-1">mdi-alert-circle</v-icon>
              {{ subjectGenerateError }}
            </div>
          </div>
        </div>

        <!-- Footer actions -->
        <div class="new-msg-footer">
          <v-btn color="default" dark @click="newMessageDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="success"
            :loading="sending"
            :disabled="!canSendNewMessage"
            @click="sendNewMessage"
          >
            <v-icon left>mdi-send</v-icon>
            Send Message
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="textMessageDialog" max-width="400">
      <v-card class="pa-3">
        <div class="headline">Text Message</div>

        <v-btn
          small
          class="mr-2 mt-4"
          outline
          color="success"
          :loading="uploading"
          @click="pickTextMessageFile()"
        >
          Upload
          <v-icon right dark>mdi-cloud-upload</v-icon>
        </v-btn>
        <input
          type="file"
          style="display: none"
          ref="textMessageFiles"
          accept=".mp3"
          @change="onTextMessageFilePicked($event)"
        />
        <div v-if="textMessageFiles.length" class="mt-0 ml-2 mb-2">
          {{ textMessageFiles[0].name }}
        </div>
        <v-text-field
          class="mt-2 mb-3"
          v-model="textMessage.phone"
          hide-details
          label="From Phone"
        ></v-text-field>
        <v-textarea
          class="mt-2 mb-3"
          v-model="textMessage.text"
          rows="8"
          outline
          hide-details
          label="Text"
          ref="textMessageText"
        ></v-textarea>
        <img
          class="mb-3"
          v-if="textMessage.imageUrl"
          :src="textMessage.imageUrl"
          style="width: 100%;"
        />
        <v-card-actions class="mr-0 mt-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="textMessageDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="success"
            :loading="sending"
            :disabled="!textMessage.text"
            @click="sendTextMessage"
            >Send Message</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteMessagesDialog" max-width="400">
      <v-card class="pa-3">
        <div class="headline">Delete Messages</div>
        <div class="my-3">This will delete {{ selected.length }} messages</div>
        <v-card-actions class="mr-0 mt-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="deleteMessagesDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="success" :loading="processing" @click="deleteMessages"
            >Delete</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="viewMessageDialog" max-width="1000">
      <v-card class="view-msg-card">
        <!-- Header -->
        <div class="view-msg-header">
          <v-icon color="white" class="mr-2">mdi-message-text</v-icon>
          <div>
            <div class="view-msg-header-title">Message Details</div>
            <div
              class="view-msg-header-sub"
              v-if="clickedMessage && clickedMessage.SenderName"
            >
              {{ clickedMessage.SenderName }}
              <span v-if="clickedMessage.SenderPhone"
                >&nbsp;&bull;&nbsp;{{ clickedMessage.SenderPhone }}</span
              >
              <span v-if="clickedMessage.Date"
                >&nbsp;&bull;&nbsp;{{ clickedMessage.Date | date }}</span
              >
            </div>
          </div>
        </div>

        <div class="view-msg-body">
          <!-- two-column grid -->
          <div class="view-msg-cols">
            <!-- ══ LEFT COLUMN ══ -->
            <div class="view-msg-col">
              <!-- ── Recordings ── -->
              <div class="view-msg-section">
                <div class="view-msg-section-label">
                  <v-icon small class="mr-1" color="success"
                    >mdi-volume-high</v-icon
                  >
                  Recordings
                </div>
                <div
                  v-if="clickedMessage && clickedMessage.RecordingUrl"
                  class="mb-3"
                >
                  <div class="view-msg-sublabel">Message</div>
                  <audio
                    controls
                    :src="clickedMessage.RecordingUrl"
                    class="view-msg-audio"
                  ></audio>
                </div>
                <div v-if="clickedMessage && clickedMessage.SubjectUrl">
                  <div class="view-msg-sublabel">Subject</div>
                  <audio
                    controls
                    :src="clickedMessage.SubjectUrl"
                    class="view-msg-audio"
                  ></audio>
                </div>
              </div>

              <v-divider class="my-4"></v-divider>

              <!-- ── Subject ── -->
              <div class="view-msg-section">
                <div class="view-msg-section-label">
                  <v-icon small class="mr-1" color="success"
                    >mdi-text-box-outline</v-icon
                  >
                  Subject
                </div>

                <!-- Transcribed text -->
                <div
                  v-if="clickedMessage && clickedMessage.SubjectText"
                  class="view-msg-subject-text-wrap mb-3"
                >
                  <div class="view-msg-sublabel mb-1">Transcribed Text</div>
                  <div
                    class="view-msg-subject-html"
                    v-html="clickedMessage.SubjectText"
                  ></div>
                </div>

                <!-- Title edit -->
                <div class="view-msg-sublabel mb-1">Display Title</div>
                <div class="view-msg-title-row">
                  <v-textarea
                    v-model="clickedMessageSubjectTitle"
                    hide-details
                    outline
                    auto-grow
                    rows="1"
                    label="Subject title..."
                    class="view-msg-title-field"
                  ></v-textarea>
                  <v-btn
                    color="success"
                    :loading="updatingSubjectTitle"
                    :disabled="!clickedMessage || !clickedMessage.Id"
                    @click="updateSubjectTitle"
                    style="align-self: flex-start; margin-top: 4px;"
                  >
                    <v-icon left small>mdi-content-save</v-icon>
                    Save
                  </v-btn>
                </div>
                <div
                  v-if="subjectTitleWordCount > 10"
                  class="mt-1 view-msg-word-warning"
                >
                  <v-icon small color="warning" class="mr-1">mdi-alert</v-icon>
                  {{ subjectTitleWordCount }} words &mdash; try to keep the
                  title under 10 words.
                </div>
                <div
                  v-if="subjectTitleUpdateMessage"
                  class="mt-2 view-msg-status-text"
                  :class="
                    subjectTitleUpdateError ? 'error--text' : 'success--text'
                  "
                >
                  <v-icon
                    small
                    :color="subjectTitleUpdateError ? 'error' : 'success'"
                    class="mr-1"
                  >
                    {{
                      subjectTitleUpdateError
                        ? "mdi-alert-circle"
                        : "mdi-check-circle"
                    }}
                  </v-icon>
                  {{ subjectTitleUpdateMessage }}
                </div>
              </div>
            </div>
            <!-- /left col -->

            <!-- ══ RIGHT COLUMN ══ -->
            <div class="view-msg-col">
              <!-- ── Listeners ── -->
              <div class="view-msg-section">
                <div class="view-msg-section-label">
                  <v-icon small class="mr-1" color="success"
                    >mdi-account-multiple</v-icon
                  >
                  Listeners
                </div>
                <v-progress-linear
                  v-if="loading"
                  :indeterminate="true"
                  color="success"
                  height="3"
                  class="mb-3"
                  style="border-radius: 3px;"
                ></v-progress-linear>
                <div
                  v-if="
                    !loading &&
                      clickedMessage &&
                      clickedMessage.listeners &&
                      clickedMessage.listeners.length
                  "
                  class="view-msg-listeners"
                >
                  <div
                    v-for="(info, index) in clickedMessage.listeners"
                    :key="index"
                  >
                    <div class="view-msg-listener-row">
                      <div class="view-msg-listener-name">{{ info.name }}</div>
                      <div class="view-msg-listener-time">{{ info.time }}</div>
                      <div class="view-msg-listener-last">
                        {{ info.lastTime }}
                      </div>
                    </div>
                    <v-divider
                      v-if="index + 1 < clickedMessage.listeners.length"
                      class="my-1"
                    ></v-divider>
                  </div>
                </div>
                <div
                  v-if="
                    !loading &&
                      clickedMessage &&
                      (!clickedMessage.listeners ||
                        !clickedMessage.listeners.length)
                  "
                  class="view-msg-empty"
                >
                  No listeners yet.
                </div>
              </div>

              <v-divider class="my-4"></v-divider>

              <!-- ── Replace Recording ── -->
              <div class="view-msg-section">
                <div class="view-msg-section-label">
                  <v-icon small class="mr-1" color="success"
                    >mdi-swap-horizontal</v-icon
                  >
                  Replace Recording (with fast speed)
                </div>
                <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                  <v-btn
                    outline
                    color="success"
                    :loading="uploading"
                    @click="pickClickedMessageFile()"
                  >
                    <v-icon left>mdi-cloud-upload</v-icon>
                    Upload New MP3
                  </v-btn>
                  <input
                    type="file"
                    style="display: none"
                    ref="clickedMessageFiles"
                    accept=".mp3"
                    @change="onClickedMessageFilePicked($event)"
                  />
                  <span
                    v-if="
                      clickedMessage &&
                        clickedMessage.files &&
                        clickedMessage.files.length
                    "
                    class="view-msg-filename"
                  >
                    <v-icon small color="success" class="mr-1"
                      >mdi-music</v-icon
                    >
                    {{ clickedMessage.files[0].name }}
                  </span>
                </div>
                <audio
                  class="view-msg-audio mt-3"
                  v-if="clickedMessage.newRecordingURL"
                  controls
                  ref="newRecordingURL"
                  :src="clickedMessage.newRecordingURL"
                ></audio>
                <div class="mt-3" v-if="clickedMessage.newRecordingURL">
                  <v-btn
                    color="success"
                    :loading="updating"
                    @click="updateMessage"
                  >
                    <v-icon left small>mdi-check</v-icon>
                    Apply New Recording
                  </v-btn>
                </div>
              </div>

              <v-divider class="my-4"></v-divider>

              <!-- ── Actions ── -->
              <div class="view-msg-section">
                <div class="view-msg-section-label">
                  <v-icon small class="mr-1" color="success"
                    >mdi-lightning-bolt</v-icon
                  >
                  Actions
                </div>
                <v-btn
                  outline
                  color="success"
                  :disabled="!clickedMessage"
                  @click="openMessageInterviewDialog(clickedMessage)"
                >
                  <v-icon left small>mdi-microphone</v-icon>
                  Add To Interview Section
                </v-btn>
              </div>
            </div>
            <!-- /right col -->
          </div>
          <!-- /cols -->

          <v-divider class="mt-4 mb-3"></v-divider>

          <!-- ── Raw Data (collapsible, full-width) ── -->
          <div class="view-msg-section">
            <div
              class="view-msg-section-label view-msg-raw-toggle"
              @click="showRawData = !showRawData"
              style="cursor: pointer;"
            >
              <v-icon small class="mr-1" color="grey">mdi-code-json</v-icon>
              <span style="color: #888;">Raw Data</span>
              <v-icon small class="ml-1" color="grey">
                {{ showRawData ? "mdi-chevron-up" : "mdi-chevron-down" }}
              </v-icon>
            </div>
            <pre v-if="showRawData" class="view-msg-raw">{{
              viewMessageText | pretty
            }}</pre>
          </div>
        </div>

        <!-- Footer -->
        <div class="view-msg-footer">
          <v-btn @click="viewMessageDialog = false" color="#888" dark>
            <v-icon left small>mdi-close</v-icon>
            Close
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="updateInterviewMessagesDialog" max-width="400">
      <v-card class="pa-3">
        <div class="headline">Add To Interview Section</div>
        <div class="my-3 font-weight-medium">
          This message will be added to the interview section at #3-3
        </div>
        <v-layout row>
          <div class="font-weight-medium mr-2">Date:</div>
          {{ clickedMessage.Date | date }}</v-layout
        ><v-layout row>
          <div class="font-weight-medium mr-2 mb-2">Name:</div>
          {{ clickedMessage.SenderName }}</v-layout
        >
        <audio
          class="mt-2 mb-3"
          v-if="clickedMessage.RecordingUrl"
          controls
          ref="recordingURL"
          :src="clickedMessage.RecordingUrl"
        ></audio>
        <v-textarea
          class="mt-2 mb-3"
          label="Interview Subject"
          v-model="interviewMessageSubject"
          rows="2"
          outline
          hide-details
        ></v-textarea>
        <v-btn
          class="mt-0 mb-3 py-0"
          small
          :disabled="!interviewMessageSubject"
          @click="playText(interviewMessageSubject)"
          color="info"
        >
          Play
        </v-btn>
        <v-card-actions class="mr-0 mt-0">
          <v-spacer></v-spacer>
          <v-btn
            color="default"
            dark
            @click="updateInterviewMessagesDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="success"
            :loading="processing"
            @click="saveInterviewMessage"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="playlistDialog" max-width="600">
      <v-card class="pa-3">
        <div class="headline">Save Messages To Playlist</div>
        <div class="my-3">
          This will save the {{ selected.length }} selected messages into the
          playlist
        </div>
        <div style="overflow-y: auto; height: 350px">
          <div v-for="(message, index) in selected">
            <div class="font-weight-medium">
              {{ formatDate(message.date) }}
            </div>
            <div class="mb-2">
              {{ message.senderName + " - " + message.senderPhone }}
            </div>
            <audio :src="message.recordingUrl" controls></audio>
            <v-divider
              class="my-2"
              v-if="index + 1 < selected.length"
            ></v-divider>
          </div>
        </div>
        <v-text-field
          v-model="playlistCallerID"
          label="Caller ID"
          hide-details
          style="width: 300px;"
          class="mb-2"
        ></v-text-field>
        <v-card-actions class="mr-0 mt-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="playlistDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="success"
            :loading="processing"
            :disabled="invalidPhone(playlistCallerID)"
            @click="saveToPlaylist"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ky from "ky";
import * as firebase from "firebase/app";
import "firebase/database";
import moment from "moment";

export default {
  data() {
    return {
      title: "Credit",
      rowsPerPageItems: [10, 20],
      pagination: {
        totalItems: 0,
        rowsPerPage: 10,
        page: 1,
      },

      isMobile: false,
      selected: [],

      headers: [
        { value: "Status", text: "Status" },
        { value: "Timestamp", text: "Timestamp" },
        { value: "SenderName", text: "Sender" },
        { value: "SenderPhone", text: "Sender Phone" },
        { value: "RecordingURL", text: "Recording" },
        { value: "OriginalRecordingURL", text: "Original" },
        { value: "phoneNumberList", text: "Sent To" },
      ],

      messages: [],

      viewMessageDialog: false,
      updateInterviewMessagesDialog: false,
      interviewMessageSubject: "",
      loading: false,
      viewMessageText: "",
      clickedMessage: "",
      clickedMessageSubjectTitle: "",

      newMessageDialog: false,
      textMessageDialog: false,

      deleteMessagesDialog: false,
      updating: false,
      updatingSubjectTitle: false,
      uploading: false,
      sending: false,
      processing: false,
      files: [],
      textMessageFiles: [],
      uploadFileError: "",
      messageGenerating: false,
      messageGenerateError: "",
      subjectGenerating: false,
      subjectGenerateError: "",
      subjectTitleUpdateMessage: "",
      subjectTitleUpdateError: false,
      showRawData: false,

      playlistDialog: false,
      playlistCallerID: "",

      newMessage: {
        recordingMode: "upload",
        recordingURL: "",
        recordingDuration: 0,
        recordingText: "",
        subject: "",
        subjectUrl: "",
        phone: "13479710707",
      },

      textMessage: {
        imageUrl: "",
        phone: "13479710707",
      },

      actions: [],
      intervalID: "",

      columns: [
        {
          field: "Status",
          isIcon: true,
          template: "icon",
          alignment: "center",
          visible: true,
          allowFiltering: false,
          allowHeaderFiltering: false,
          allowHiding: false,
          allowSearch: false,
          cssClass: "width-25",
          headerFilter: {
            allowSearch: false,
            groupInterval: undefined,
            height: undefined,
            searchMode: "contains",
            width: undefined,
          },
          icon: (value) => {
            switch (value) {
              case "Completed":
                return "mdi-check-circle";
              case "InProgress":
              case "Queued":
                return "mdi-progress-upload";
              case "Pending":
                return "mdi-clock";
              default:
                return "";
            }
          },
          iconColor: (value) => {
            switch (value) {
              case "Completed":
                return "success";
              case "InProgress":
              case "Queued":
                return "#42A5F5";
              case "Pending":
                return "#fdb400";
              default:
                return "#aeb8bf";
            }
          },
        },
        {
          field: "SenderName",
          label: "Name",
          format: "",
          visible: true,
          filterType: "",
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          field: "SenderPhone",
          label: "From Phone",
          format: "",
          visible: true,
          filterType: "",
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          field: "Date",
          label: "Date",
          type: "date",
          format: "",
          visible: true,
          filterType: "",
          format: "MM/dd/yyyy hh:mm:ss a",
          sortOrder: "desc",
          sortIndex: 0,
          filterValues: [],
          filterValue: new Date(
            moment()
              .subtract(30, "day")
              .add(1, "day")
          ),
          selectedFilterOperation: ">=",
        },
        {
          field: "RecordingUrl",
          label: "Recording",
          format: "",
          template: "link-audio-cell-template",
          visible: true,
          filterType: "",
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          field: "SubjectUrl",
          label: "Subject",
          format: "",
          template: "link-audio-cell-template",
          visible: true,
          filterType: "",
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          field: "Parts",
          label: "Parts",
          type: "number",
          visible: false,
          filterType: "",
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          field: "Status",
          label: "Status",
          format: "",
          visible: true,
          filterType: "",
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          field: "FormattedDuration",
          label: "Duration",
          format: "",
          visible: true,
          filterType: "",
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          field: "SubjectTitle",
          label: "Subject Title",
          format: "",
          visible: true,
          filterType: "",
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          field: "Type",
          label: "Type",
          format: "",
          visible: true,
          filterType: "",
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
        {
          field: "State",
          label: "Interview Subject",
          format: "",
          visible: true,
          filterType: "",
          filterValues: [],
          filterValue: null,
          selectedFilterOperation: null,
        },
      ],
    };
  },
  computed: {
    pages() {
      return this.pagination.rowsPerPage
        ? Math.ceil(this.pagination.totalItems / this.pagination.rowsPerPage)
        : 0;
    },
    phoneValid() {
      const digits = (this.newMessage.phone || "").replace(/\D/g, "");
      return digits.length === 10 || digits.length === 11;
    },
    normalizedPhone() {
      const digits = (this.newMessage.phone || "").replace(/\D/g, "");
      if (digits.length === 10) return "1" + digits;
      if (digits.length === 11 && digits[0] !== "1")
        return "1" + digits.slice(1);
      return digits;
    },
    subjectTitleWordCount() {
      const title = this.clickedMessageSubjectTitle || "";
      return title.trim() === "" ? 0 : title.trim().split(/\s+/).length;
    },
    canSendNewMessage() {
      const hasRecording = !!this.newMessage.recordingURL;
      const hasValidSubject =
        !this.newMessage.subject || !!this.newMessage.subjectUrl;
      return (
        hasRecording &&
        hasValidSubject &&
        this.phoneValid &&
        !this.subjectGenerating &&
        !this.messageGenerating
      );
    },
  },
  filters: {
    pretty: function(value) {
      if (!value) return "";

      return JSON.stringify(value, null, 2);
    },
    date: function(value) {
      if (value) {
        return moment(value).format("MM/DD/YYYY h:mm:ss A");
      }
    },
  },
  mounted() {
    this.intervalID = setInterval(() => {
      if (this.$refs && this.$refs.grid) {
        this.$refs.grid.reloadGrid();
      }
    }, 300000);
  },
  created() {
    //this.firebaseSetup();
    //this.loadAccount();
    //this.loadData();
    this.isMobile = window.innerWidth < 551;

    this.actions.push({
      action: this.openTextMessageDialog,
      text: "Text Message",
    });

    this.actions.push({
      action: this.openNewMessageDialog,
      text: "New Message",
    });

    this.actions.push({
      action: this.openDeleteMessages,
      showOnSelection: true,
      text: "Delete Messages",
    });

    this.actions.push({
      action: this.openPlaylistDialog,
      showOnSelection: true,
      text: "Save To Playlist",
    });

    this.setTextMessagePhone();

    window.addEventListener("paste", (e) => {
      if (this.textMessageDialog) {
        this.$refs.textMessageFiles.files = e.clipboardData.files;
        this.textMessageFiles = e.clipboardData.files;

        this.uploadTextMessageFile(e.clipboardData.files);
      }
    });
  },
  beforeDestroy() {
    clearInterval(this.intervalID);
  },
  methods: {
    firebaseSetup() {
      const firebaseConfig = {
        apiKey: "Xs6kU5UjjL8n5LdLQJMGnWcaEietBLrj5U9FnpIb",
        authDomain: "broadcast-b7a56.firebaseapp.com",
        databaseURL: "https://broadcast-b7a56.firebaseio.com",
        projectId: "broadcast-b7a56",
      };

      // Initialize Firebase
      if (!firebase.apps.length) {
        firebase.initializeApp(firebaseConfig);
      }

      var database = firebase.database();
      var ref = firebase.database().ref("messages");

      const messagesRef = ref.orderByKey().limitToLast(100);

      messagesRef.on(
        "value",
        (snapshot) => {
          const messages = Object.values(snapshot.val()).map((message) => {
            message.SenderPhone = this.formatPhoneNumber(message.SenderPhone);

            message.PhoneNumberList = message.PhoneNumbers.map((m) =>
              this.formatPhoneNumber(m.Phone)
            ).join(",");

            message.Date = moment(message.Timestamp).format("LLL");

            return message;
          });
          this.messages = messages.sort((a, b) => {
            if (Date.parse(a.Timestamp) > Date.parse(b.Timestamp)) return -1;
            if (Date.parse(a.Timestamp) < Date.parse(b.Timestamp)) return 1;
            return 0;
          });

          this.pagination.totalItems = this.messages.length;
        },
        function(error) {
          console.log("Error: " + error.code);
        }
      );

      ref.on("child_added", (data, prevChildKey) => {
        // const message = data.val();
        // message.SenderPhone = this.formatPhoneNumber(
        //     message.SenderPhone
        // );
        // message.PhoneNumberList = message.PhoneNumbers.map(m =>
        //     this.formatPhoneNumber(m.Phone)
        // ).join(",");
        // message.Timestamp = moment(message.Timestamp).format("LLL");
        // this.messages.push(message);
      });

      // ref.on("child_changed", function(data, prevChildKey) {
      //     console.log(data.val());
      // });

      var accountRef = firebase.database().ref("account");

      const accounts = accountRef.orderByKey().limitToLast(1);

      accounts.on(
        "value",
        (snapshot) => {
          const accountInfo = Object.values(snapshot.val()).map((account) => {
            this.title = "Credit: $" + account.Credit.toFixed(2);

            return account;
          });
        },
        function(error) {
          console.log("Error: " + error.code);
        }
      );
    },
    openMessageInterviewDialog() {
      this.updateInterviewMessagesDialog = true;
    },
    getLastMessages() {
      return this.messages.slice(0, 30);
    },
    formatPhoneNumber(phoneNumberString) {
      var cleaned = ("" + phoneNumberString).replace(/\D/g, "");
      var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return "(" + match[1] + ") " + match[2] + "-" + match[3];
      }
      return null;
    },
    getBaseUrl() {
      return window.location.origin.startsWith("http://localhost")
        ? "https://localhost:5001"
        : "https://wercbergerproductions-app-164903695581.us-east1.run.app";
    },
    async loadData() {
      const messages = await ky("api/home/getMessages", {
        prefixUrl: this.getDomain(),
      }).json();

      this.pagination.totalItems = messages.length;
      this.messages = messages;
    },
    async loadAccount() {
      const account = await ky("api/home/getAccount", {
        prefixUrl: this.getDomain(),
      }).json();

      const credit = +JSON.parse(account).cash_credits;

      this.title = "Credit: $" + credit.toFixed(2);
    },
    getDomain() {
      return window.location.host.includes("localhost")
        ? "https://localhost:44337"
        : "https://broadcastmessageswercber.azurewebsites.net";
    },
    getStatusIcon(status) {
      switch (status) {
        case "Completed":
          return "mdi-check-circle";
        case "InProgress":
        case "Queued":
          return "mdi-progress-upload";
        case "Pending":
          return "mdi-clock";
      }
    },
    getStatusIconColor(status) {
      switch (status) {
        case "Completed":
          return "success";
        case "InProgress":
        case "Queued":
          return "#42A5F5";
        case "Pending":
          return "#fdb400";
      }
    },
    onRowClicked(row) {
      const message = JSON.parse(JSON.stringify(row));

      //message.recordingURL = message.recordingURL;

      this.viewMessage(message);
    },
    // onLoaded(result) {
    //     result = result.map((r) => {
    //         return this.parsePascalCaseToCamelCase(r);
    //     });
    //     console.log(result);
    // },
    async viewMessage(message) {
      this.clickedMessage = message;
      this.clickedMessage.files = [];
      this.clickedMessageSubjectTitle = message.SubjectTitle || "";
      this.subjectTitleUpdateMessage = "";
      this.subjectTitleUpdateError = false;
      this.showRawData = false;
      this.viewMessageDialog = true;

      this.viewMessageText = message;

      try {
        this.loading = true;

        const url =
          this.getDomain() +
          "/api/broadcast/GetListenersSql?recordingURL=" +
          message.RecordingUrl;

        const response = await this.$http.get(url);

        this.clickedMessage.listeners = response.data.listeners.map(
          (listener) => {
            listener.lastTime = moment(listener.lastTime).format(
              "MM/DD/YYYY h:mm:ss A"
            );

            return listener;
          }
        );

        //this.clickedMessage = message;
        this.clickedMessage.newRecordingURL = "";

        this.viewMessageText = this.clickedMessage;

        console.log(JSON.stringify(this.clickedMessage, null, 2));

        this.loading = false;
      } catch (e) {
        this.loading = false;
      }
    },
    async updateSubjectTitle() {
      if (!this.clickedMessage || !this.clickedMessage.Id) return;

      this.updatingSubjectTitle = true;
      this.subjectTitleUpdateMessage = "";
      this.subjectTitleUpdateError = false;

      try {
        const title = this.clickedMessageSubjectTitle || "";
        const response = await this.$http.put(
          this.getBaseUrl() +
            "/api/messages/" +
            this.clickedMessage.Id +
            "/subjecttitle",
          JSON.stringify(title),
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        this.clickedMessage.SubjectTitle = title;
        this.viewMessageText = this.clickedMessage;
        this.subjectTitleUpdateMessage =
          "Updated subject title for " +
          (response &&
          response.data &&
          response.data.updated !== null &&
          response.data.updated !== undefined
            ? response.data.updated
            : 0) +
          " message(s).";
        this.$refs.grid.reloadGrid();
      } catch (e) {
        this.subjectTitleUpdateError = true;
        this.subjectTitleUpdateMessage = "Failed to update subject title.";
      } finally {
        this.updatingSubjectTitle = false;
      }
    },
    openNewMessageDialog() {
      const user = this.$store.state.user;

      switch (user.toLowerCase()) {
        case "aronyakov":
          this.newMessage.phone = "13474366239";
          break;
        case "saul":
          this.newMessage.phone = "19176856104";
          break;
        case "tatty":
          this.newMessage.phone = "17185706574";
          break;
        case "yakov":
          this.newMessage.phone = "13475540115";
          break;
        default:
          break;
      }

      this.newMessageDialog = true;
      this.newMessage.recordingMode = "upload";
      this.newMessage.recordingURL = "";
      this.newMessage.recordingDuration = 0;
      this.newMessage.recordingText = "";
      this.newMessage.subjectUrl = "";
      this.uploadFileError = "";
      this.messageGenerateError = "";
      this.subjectGenerateError = "";
    },
    onRecordingModeChanged(mode) {
      this.newMessage.recordingURL = "";
      this.newMessage.recordingDuration = 0;
      this.uploadFileError = "";
      this.messageGenerateError = "";

      if (mode === "upload") {
        this.newMessage.recordingText = "";
      } else {
        this.files = [];
        if (this.$refs.files) {
          this.$refs.files.value = null;
        }
      }
    },
    onNewMessageRecordingTextChanged() {
      this.newMessage.recordingURL = "";
      this.newMessage.recordingDuration = 0;
      this.files = [];
      if (this.$refs.files) {
        this.$refs.files.value = null;
      }
      this.uploadFileError = "";
      this.messageGenerateError = "";
    },
    onNewMessageSubjectChanged() {
      this.newMessage.subjectUrl = "";
      this.subjectGenerateError = "";
    },
    onPhoneInput() {
      // strip non-digits so user can paste formatted numbers freely
      this.newMessage.phone = this.newMessage.phone.replace(/[^\d]/g, "");
    },
    onRecordingMetadataLoaded() {
      if (!this.$refs.recordingURL) return;

      const duration = Number(this.$refs.recordingURL.duration || 0);
      this.newMessage.recordingDuration =
        Number.isFinite(duration) && duration > 0
          ? Math.round(duration * 1000)
          : 0;
    },
    setTextMessagePhone() {
      const user = this.$store.state.user;

      switch (user.toLowerCase()) {
        case "aronyakov":
          this.textMessage.phone = "13474366239";
          break;
        case "saul":
          this.textMessage.phone = "19176856104";
          break;
        case "tatty":
          this.textMessage.phone = "17185706574";
          break;
        case "yakov":
          this.textMessage.phone = "13475540115";
          break;
        default:
          break;
      }
    },
    openTextMessageDialog() {
      this.textMessageDialog = true;
    },
    openDeleteMessages() {
      this.selected = this.$refs.grid.getSelectedRowsData();
      this.deleteMessagesDialog = true;
    },
    async deleteMessages() {
      try {
        this.processing = true;

        await this.$http.post(
          this.getDomain() + "/api/broadcast/deleteMessages",
          this.selected.map((s) => s.RecordingUrl)
        );

        this.processing = false;
        this.deleteMessagesDialog = false;
        this.$refs.grid.reloadGrid();
      } catch (e) {
        this.processing = false;
      }
    },
    async saveInterviewMessage() {
      if (!this.interviewMessageSubject) return;

      try {
        this.processing = true;

        await this.$http.post(
          this.getDomain() +
            "/api/broadcast/UpdateInterviewMessage?originalURL=" +
            this.clickedMessage.RecordingUrl +
            "&subject=" +
            this.interviewMessageSubject
        );

        this.processing = false;
        this.updateInterviewMessagesDialog = false;
        this.$refs.grid.reloadGrid();
      } catch (e) {
        this.processing = false;
      }
    },
    pickClickedMessageFile() {
      this.$refs.clickedMessageFiles.click();
    },
    async onClickedMessageFilePicked(e) {
      this.uploading = true;

      const files = e.target.files;

      if (!files) return;

      this.clickedMessage.files = files;

      var formData = new FormData();

      formData.append("file", files[0]);

      try {
        const response = await this.$http.post(
          this.getDomain() + "/api/home/GetUrl?type=message",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        this.clickedMessage.newRecordingURL = response.data;

        this.uploading = false;
      } catch (e) {
        this.uploading = false;
      }
    },
    async updateMessage() {
      if (!this.$refs.newRecordingURL) return;

      this.updating = true;

      try {
        await this.$http.post(
          this.getDomain() +
            "/api/broadcast/updateMessageSpeed?originalRecordingUrl=" +
            this.clickedMessage.RecordingUrl +
            "&newRecordingUrl=" +
            this.clickedMessage.newRecordingURL
        );

        this.updating = false;
        this.viewMessageDialog = false;

        this.clickedMessage.files = [];
        this.clickedMessage.newRecordingUrl = "";
      } catch (e) {
        this.sending = false;
      }
    },
    pickFile() {
      this.$refs.files.click();
    },
    async onFilePicked(e) {
      this.uploading = true;
      this.uploadFileError = "";

      const files = e.target.files;

      if (!files || !files.length) {
        this.uploading = false;
        return;
      }

      this.files = files;

      var formData = new FormData();

      formData.append("file", files[0]);

      try {
        const response = await this.$http.post(
          this.getDomain() + "/api/home/GetUrl?type=greeting",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        this.newMessage.recordingURL = response.data;
        this.newMessage.recordingDuration = 0;
        this.newMessage.recordingMode = "upload";
        this.newMessage.recordingText = "";
        this.uploadFileError = "";
        this.messageGenerateError = "";

        this.uploading = false;
      } catch (e) {
        const sizeInBytes = files[0] ? files[0].size : 0;
        const sizeInMb = sizeInBytes
          ? (sizeInBytes / (1024 * 1024)).toFixed(2)
          : "unknown";
        const status = e && e.response ? e.response.status : null;
        const errorText =
          (e && e.response && e.response.data && String(e.response.data)) ||
          (e && e.message ? e.message : "");
        const looksTooLarge =
          status === 413 || /too\s*large|maximum|size|payload/i.test(errorText);

        if (looksTooLarge) {
          this.uploadFileError =
            "Upload failed. File size is " +
            sizeInMb +
            " MB and may be too big.";
        } else {
          this.uploadFileError = "";
        }

        this.uploading = false;
      }
    },
    pickTextMessageFile() {
      this.$refs.textMessageFiles.click();
    },
    async onTextMessageFilePicked(e) {
      this.uploading = true;

      const textMessageFiles = e.target.files;

      if (!textMessageFiles) return;

      this.textMessageFiles = textMessageFiles;

      this.uploadTextMessageFile(textMessageFiles);
    },
    async uploadTextMessageFile(files) {
      var formData = new FormData();

      formData.append("file", files[0]);

      try {
        const response = await this.$http.post(
          this.getDomain() + "/api/home/GetImageUrl?type=greeting",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        this.textMessage.imageUrl = response.data;

        this.uploading = false;
      } catch (e) {
        this.uploading = false;
      }
    },
    async sendTextMessage() {
      // if (!this.$refs.recordingURL || !this.$refs.recordingURL.duration)
      //     return;

      this.sending = true;

      try {
        await this.$http.post(
          this.getDomain() + "/api/broadcast/sendTextMessage",
          {
            urls: this.textMessage.imageUrl,
            to: this.textMessage.phone,
            text: this.textMessage.text,
          }
        );

        this.sending = false;
        this.textMessageFiles = [];
        this.$refs.textMessageFiles.value = null;
        this.textMessageDialog = false;

        this.textMessage.imageUrl = "";
        this.textMessage.text = "";
      } catch (e) {
        this.sending = false;
      }
    },
    async sendNewMessage() {
      if (!this.$refs.recordingURL) return;
      if (this.newMessage.subject && !this.newMessage.subjectUrl) return;

      this.sending = true;

      try {
        const duration =
          this.newMessage.recordingDuration ||
          Math.round(Number(this.$refs.recordingURL.duration || 0) * 1000);
        if (!duration) {
          this.sending = false;
          return;
        }
        const sendUrl =
          this.getBaseUrl() +
          "/api/broadcast/SaveMessageAsQueued?recordingurl=" +
          encodeURIComponent(this.newMessage.recordingURL) +
          "&fromPhone=" +
          encodeURIComponent(this.normalizedPhone) +
          "&subjectUrl=" +
          encodeURIComponent(this.newMessage.subjectUrl || "") +
          "&recordingDuration=" +
          encodeURIComponent(duration) +
          "&callUUID=manually";

        await this.$http.post(sendUrl);

        this.sending = false;
        this.newMessageDialog = false;

        this.newMessage.recordingURL = "";
        this.newMessage.recordingDuration = 0;
        this.newMessage.recordingText = "";
        this.newMessage.subject = "";
        this.newMessage.subjectUrl = "";
        this.messageGenerateError = "";
        this.subjectGenerateError = "";
      } catch (e) {
        this.sending = false;
      }
    },
    async generateSubjectAudioUrl(text) {
      const response = await this.$http.post(
        this.getBaseUrl() + "/api/broadcast/GetAudioForText",
        {
          text: text,
        }
      );

      return response && response.data ? response.data.azureSubjectUrl : "";
    },
    async generateNewMessageSubjectAudio() {
      if (!this.newMessage.subject) return;

      this.subjectGenerating = true;
      this.subjectGenerateError = "";

      try {
        const subjectUrl = await this.generateSubjectAudioUrl(
          this.newMessage.subject
        );
        this.newMessage.subjectUrl = subjectUrl || "";

        if (!this.newMessage.subjectUrl) {
          this.subjectGenerateError =
            "Could not generate subject audio. Please try again.";
          return;
        }
      } catch (e) {
        this.subjectGenerateError =
          "Failed to generate subject audio. Please try again.";
      } finally {
        this.subjectGenerating = false;
      }
    },
    async generateNewMessageRecordingAudio() {
      if (!this.newMessage.recordingText) return;

      this.messageGenerating = true;
      this.messageGenerateError = "";

      try {
        const recordingUrl = await this.generateSubjectAudioUrl(
          this.newMessage.recordingText
        );
        this.newMessage.recordingURL = recordingUrl || "";
        this.newMessage.recordingDuration = 0;
        this.files = [];

        if (!this.newMessage.recordingURL) {
          this.messageGenerateError =
            "Could not generate message audio. Please try again.";
          return;
        }
      } catch (e) {
        this.messageGenerateError =
          "Failed to generate message audio. Please try again.";
      } finally {
        this.messageGenerating = false;
      }
    },
    openPlaylistDialog() {
      this.selected = this.$refs.grid.getSelectedRowsData();

      this.playlistDialog = true;
    },
    async saveToPlaylist() {
      const phone =
        this.playlistCallerID && this.playlistCallerID.length < 11
          ? "1" + this.playlistCallerID
          : this.playlistCallerID;

      this.processing = true;

      try {
        await this.$http.post(
          this.getDomain() + "/api/playlist/saveMessagesToPlaylist",
          {
            ids: this.selected.map((i) => i.Id),
            callerID: phone,
          }
        );

        this.processing = false;
        this.playlistDialog = false;

        this.playlistCallerID = "";
      } catch (e) {
        this.sending = false;
      }
    },
    formatDate(date) {
      if (!date) return;

      return moment(date).format("L");
    },
    invalidPhone(phone) {
      var numbersOnly = phone.replace(/\D/g, "");

      return numbersOnly.length < 10 || numbersOnly.length > 11;
    },
    playText(text) {
      this.generateSubjectAudioUrl(text)
        .then((subjectUrl) => {
          if (!subjectUrl) return;
          var audio = new Audio(subjectUrl);
          audio.play();
        })
        .catch(() => {
          console.log("Failed to generate and play text audio");
        });
    },
  },
};
</script>
<style>
tbody tr:nth-child(even) {
  background-color: #eee;
}
.width-25 {
  width: 25px !important;
  min-width: 25px !important;
  max-width: 25px !important;
}

/* ── New Message dialog ── */
.new-msg-card {
  overflow: hidden;
}
.new-msg-header {
  background: #5cb860;
  color: #fff;
  font-size: 20px;
  font-weight: 500;
  padding: 18px 24px;
  display: flex;
  align-items: center;
}
.new-msg-body {
  padding: 20px 24px 8px;
  max-height: 70vh;
  overflow-y: auto;
}
.new-msg-section {
  margin-bottom: 4px;
}
.new-msg-section-label {
  font-size: 13px;
  font-weight: 600;
  color: #555;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}
.new-msg-optional {
  font-weight: 400;
  text-transform: none;
  color: #999;
  margin-left: 4px;
  font-size: 12px;
  letter-spacing: 0;
}
.new-msg-textarea >>> textarea {
  font-size: 15px !important;
  line-height: 1.6 !important;
}
.new-msg-filename {
  font-size: 13px;
  color: #444;
  display: flex;
  align-items: center;
}
.new-msg-error {
  font-size: 13px;
  color: #e53935;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 2px;
}
.new-msg-error a {
  color: #e53935;
  font-weight: 600;
  text-decoration: underline;
}
.new-msg-error a:hover {
  color: #b71c1c;
}
.new-msg-generating {
  margin-top: 4px;
}
.new-msg-audio {
  width: 100%;
  height: 40px;
  display: block;
  border-radius: 6px;
}
.new-msg-phone-row {
  display: flex;
  align-items: center;
}
.new-msg-phone-input {
  height: 36px;
  width: 100%;
  padding: 0 10px;
  font-size: 14px;
  border: 1px solid #bbb;
  border-radius: 4px;
  outline: none;
  transition: border-color 0.2s;
  background: #fff;
}
.new-msg-phone-input:focus {
  border-color: #5cb860;
}
.new-msg-phone-input.new-msg-phone-error {
  border-color: #e53935;
}
.new-msg-phone-hint {
  font-size: 11px;
  color: #e53935;
  margin-top: 3px;
}
.new-msg-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 12px 20px 16px;
  border-top: 1px solid #eee;
}

/* ── View Message dialog ── */
.view-msg-card {
  overflow: hidden;
}
.view-msg-header {
  background: #5cb860;
  color: #fff;
  padding: 18px 24px;
  display: flex;
  align-items: center;
}
.view-msg-header-title {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.2;
}
.view-msg-header-sub {
  font-size: 13px;
  opacity: 0.88;
  margin-top: 2px;
}
.view-msg-body {
  padding: 20px 24px 8px;
  max-height: 80vh;
  overflow-y: auto;
}
.view-msg-cols {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 32px;
  align-items: start;
}
.view-msg-col {
  min-width: 0;
}
.view-msg-section {
  margin-bottom: 4px;
}
.view-msg-section-label {
  font-size: 12px;
  font-weight: 700;
  color: #555;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}
.view-msg-sublabel {
  font-size: 11px;
  font-weight: 600;
  color: #888;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 4px;
}
.view-msg-audio {
  width: 100%;
  height: 40px;
  display: block;
  border-radius: 6px;
}
.view-msg-title-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.view-msg-title-field {
  flex: 1;
}
.view-msg-subject-text-wrap {
  background: #f9fbe7;
  border: 1px solid #dcedc8;
  border-radius: 6px;
  padding: 10px 14px;
}
.view-msg-subject-html {
  font-size: 14px;
  line-height: 1.6;
  color: #333;
}
.view-msg-subject-html p {
  margin: 0 0 6px;
}
.view-msg-subject-html p:last-child {
  margin-bottom: 0;
}
.view-msg-word-warning {
  font-size: 12px;
  color: #f57c00;
  display: flex;
  align-items: center;
}
.view-msg-status-text {
  font-size: 13px;
  display: flex;
  align-items: center;
}
.view-msg-filename {
  font-size: 13px;
  color: #444;
  display: flex;
  align-items: center;
}
.view-msg-listeners {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 8px 12px;
  background: #fafafa;
}
.view-msg-listener-row {
  display: flex;
  align-items: center;
  padding: 4px 0;
  font-size: 14px;
}
.view-msg-listener-name {
  width: 200px;
  font-weight: 500;
}
.view-msg-listener-time {
  width: 120px;
  color: #666;
}
.view-msg-listener-last {
  color: #888;
  white-space: nowrap;
}
.view-msg-empty {
  font-size: 13px;
  color: #aaa;
  font-style: italic;
}
.view-msg-raw {
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 12px;
  font-size: 12px;
  overflow-x: auto;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-all;
}
.view-msg-footer {
  display: flex;
  justify-content: flex-end;
  padding: 12px 20px 16px;
  border-top: 1px solid #eee;
}
</style>
